import { z } from "zod";
import { PLAN_IDS, type PlanId } from "../billing/plans.js";

/**
 * `plan` is the single source of truth for what the workspace can do
 * — seat caps, billing tier, feature gating (e.g. SAML SSO is
 * enterprise-only). Values come from `@notamen/core/billing` PLAN_IDS:
 * `solo | studio | agency | enterprise`.
 *
 * Backwards-compat: pre-rename rows wrote `plan = "team"`. The
 * preprocess coerces a stored `"team"` value to `"studio"` (same seat
 * cap, same feature set). The migration in
 * `infra/scripts/migrate-team-to-studio.ts` rewrites the stored values
 * when the operator runs it; until then, this coercion keeps every
 * read path honest — but ONLY when callers actually go through this
 * schema. Most API handlers read raw DynamoDB rows + cast (no zod), so
 * `normalizePlan` below is the helper they should reach for to enforce
 * the same invariant without paying for a full Workspace.parse on
 * every read.
 */
const StoredPlan = z
  .preprocess((v) => (v === "team" ? "studio" : v), z.enum(PLAN_IDS))
  .default("solo");

// ─── Complimentary access (demo / trial) ──────────────────────────────
//
// A platform admin can put a workspace into "demo" or "trial" mode for a
// fixed time window from the admin console. Both are the SAME mechanism —
// a time-limited grant of a plan's access — differing only by `type` tag
// (intent/labelling). While the grant is active, the workspace runs at
// `comp.plan` (the grant SETS `workspace.plan` so every existing plan-gate
// and seat cap works unchanged). When it expires, the workspace is LOCKED
// (the API walls every workspace route except billing + leave, so the
// owner can still subscribe). Enforcement is lazy/on-request — computed
// from `expiresAt`, no background job.

export const COMP_TYPES = ["demo", "trial"] as const;
export type CompType = (typeof COMP_TYPES)[number];

/**
 * Plans that may be granted as a comp. Deliberately EXCLUDES `enterprise`:
 * a demo/trial must never unlock enterprise-gated SAML SSO. (Demo defaults
 * to `agency`, the top non-enterprise tier — the highest "unlimited"-feeling
 * access that keeps SSO gated.) Validate comp plans against this, NOT the
 * full `PLAN_IDS`.
 */
export const COMP_PLAN_IDS = ["solo", "studio", "agency"] as const;
export type CompPlanId = (typeof COMP_PLAN_IDS)[number];

/** Default plan per comp type when the admin doesn't pick one explicitly. */
export const COMP_DEFAULT_PLAN: Record<CompType, CompPlanId> = {
  trial: "solo",
  demo: "agency",
};

export const COMP_DURATION_UNITS = ["hours", "days"] as const;
export type CompDurationUnit = (typeof COMP_DURATION_UNITS)[number];

export const CompGrant = z.object({
  type: z.enum(COMP_TYPES),
  /** Plan applied to `workspace.plan` while the grant is active. */
  plan: z.enum(COMP_PLAN_IDS),
  /** The plan the workspace had before the grant — restored on revoke. */
  previousPlan: StoredPlan,
  /** ISO timestamp; the workspace locks at/after this instant. */
  expiresAt: z.string(),
  grantedAt: z.string(),
  /** Cognito sub of the admin who granted it. */
  grantedBy: z.string(),
});
export type CompGrant = z.infer<typeof CompGrant>;

export interface CompStatus {
  /** The grant type, or null when there's no comp on the workspace. */
  type: CompType | null;
  /** A comp exists and now < expiresAt. */
  active: boolean;
  /** A comp exists and now >= expiresAt — the workspace is LOCKED. */
  locked: boolean;
  /** ms until expiry while active; 0 when no comp or already expired. */
  remainingMs: number;
  expiresAt: string | null;
}

/**
 * Derive demo/trial status from a (possibly absent / raw) comp grant.
 *
 * Defensive on purpose: most API handlers read raw DynamoDB rows and cast
 * without a `Workspace.parse`, so `comp` arrives as an untyped object or
 * undefined. We only trust `type` + `expiresAt`, coerce a bad/missing
 * `expiresAt` to "no active grant" rather than throwing, and never assume
 * the shape is valid. A NaN/garbage `expiresAt` yields `locked: false`,
 * `active: false` — i.e. it's treated as "no usable grant", never as an
 * accidental lock.
 */
export function compStatus(
  comp: CompGrant | null | undefined,
  nowMs: number = Date.now(),
): CompStatus {
  const none: CompStatus = {
    type: null,
    active: false,
    locked: false,
    remainingMs: 0,
    expiresAt: null,
  };
  if (!comp || typeof comp !== "object") return none;
  const type = (COMP_TYPES as readonly string[]).includes(comp.type)
    ? (comp.type as CompType)
    : null;
  const expiresAtRaw = typeof comp.expiresAt === "string" ? comp.expiresAt : "";
  const expiryMs = Date.parse(expiresAtRaw);
  if (type === null || Number.isNaN(expiryMs)) return none;
  const active = nowMs < expiryMs;
  return {
    type,
    active,
    locked: !active,
    remainingMs: active ? expiryMs - nowMs : 0,
    expiresAt: expiresAtRaw,
  };
}

// ─── Stripe subscription summary ──────────────────────────────────────
//
// The webhook denormalizes a small subscription summary onto the workspace
// META row so the access gate reads ONE row (getWorkspace) on the hot path.
// The canonical shape + the defensive `subscriptionStatus` reader live in
// `@notamen/core/billing` (subscription.ts) — browser-safe, no Stripe SDK. Here we
// only need a LOOSE zod arm so an existing row that already carries a
// `subscription` object still parses; the API reads the typed shape via
// `subscriptionStatus`, which tolerates undefined/garbage. `.passthrough()`
// keeps any extra fields the summary may carry without failing the parse.
export const SubscriptionSummarySchema = z
  .object({
    subscriptionId: z.string(),
    plan: StoredPlan,
    cadence: z.enum(["monthly", "annual"]),
    status: z.string(),
    currentPeriodEnd: z.string().optional(),
    cancelAtPeriodEnd: z.boolean().optional(),
    scheduledPlan: StoredPlan.optional(),
    scheduledCadence: z.enum(["monthly", "annual"]).optional(),
    updatedAt: z.string(),
  })
  .passthrough();

export const Workspace = z.object({
  id: z.string().regex(/^[a-z0-9][a-z0-9-]*$/),
  name: z.string(),
  plan: StoredPlan,
  createdAt: z.string(),
  createdBy: z.string(),
  /**
   * Complimentary access grant (demo/trial), set by a platform admin from
   * the admin console. Optional — absent on normal paying/free workspaces.
   * See `CompGrant` + `compStatus` below. Stored on the workspace META row.
   */
  comp: CompGrant.optional(),
  /**
   * Self-serve Stripe subscription summary, denormalized by the billing
   * webhook. Optional — absent on never-subscribed (free) workspaces, which
   * `subscriptionStatus(undefined)` correctly reports as not-locked. See
   * `@notamen/core/billing` for the typed shape + the access-gate reader.
   */
  subscription: SubscriptionSummarySchema.optional(),
});
export type Workspace = z.infer<typeof Workspace>;

/**
 * Coerce a raw stored plan value to a valid PLAN_IDS member. Use this
 * at every DynamoDB read boundary (anywhere a caller pulls a row via
 * GetCommand/ScanCommand and doesn't run Workspace.parse on it) so the
 * SPA never sees the legacy `"team"` value. Unknown inputs fall back
 * to `"solo"` — the smallest plan, so an unrecognized value never
 * grants more capability than it should and never 500s the API.
 */
export function normalizePlan(raw: unknown): PlanId {
  const result = StoredPlan.safeParse(raw);
  return result.success ? result.data : "solo";
}

export const Membership = z.object({
  workspaceId: z.string(),
  userId: z.string(),
  role: z.enum(["owner", "admin", "editor", "approver", "viewer"]),
  email: z.string().email().optional(),
  /** Scope a non-admin to specific brand ids; empty = all brands in workspace. */
  brandScope: z.array(z.string()).default([]),
  invitedAt: z.string(),
  acceptedAt: z.string().optional(),
  /** Best-effort last-activity timestamp (touched, throttled, on API use). */
  lastActiveAt: z.string().optional(),
  /**
   * Cached flag — true iff this member has a software-token MFA enrolled in
   * Cognito. Cognito remains the source of truth (the auth flow consults it
   * directly); this copy is kept up to date by the /api/me/2fa/* endpoints
   * across every workspace the user belongs to. May briefly drift if MFA is
   * toggled outside the app via the AWS console.
   */
  twoFactorEnabled: z.boolean().optional(),
});
export type Membership = z.infer<typeof Membership>;

export const Invite = z.object({
  workspaceId: z.string(),
  email: z.string().email(),
  role: z.enum(["owner", "admin", "editor", "approver", "viewer"]),
  brandScope: z.array(z.string()).default([]),
  token: z.string(),
  expiresAt: z.string(),
  invitedBy: z.string(),
});
export type Invite = z.infer<typeof Invite>;

/**
 * Role permission matrix. Used by API handlers + UI to gate actions.
 * `viewTrends` mirrors `view` (anyone who can see the brand can see trends);
 * `manageTrendSources` mirrors `generate` (connect/configure trend sources,
 * rescan, push-to-brain — the content-generation actors, not approver/viewer).
 * `generateMedia` mirrors `generate` (trigger real photo/video generation —
 * the same content-generation actors; it spends more per call but the gate is
 * the same set of roles).
 * `manageSso` is owner-only — SAML SSO configuration alters how every member
 * of the workspace signs in, so it should sit alongside other workspace-level
 * settings the admin role can't touch. Plan-level gating (enterprise only)
 * is enforced server-side on top of this.
 */
export const ROLE_PERMS = {
  owner: { manageWorkspace: true, manageBrands: true, manageMembers: true, manageSso: true, generate: true, generateMedia: true, editDraft: true, approve: true, publish: true, comment: true, view: true, viewTrends: true, manageTrendSources: true },
  admin: { manageWorkspace: false, manageBrands: true, manageMembers: true, manageSso: false, generate: true, generateMedia: true, editDraft: true, approve: true, publish: true, comment: true, view: true, viewTrends: true, manageTrendSources: true },
  editor: { manageWorkspace: false, manageBrands: false, manageMembers: false, manageSso: false, generate: true, generateMedia: true, editDraft: true, approve: false, publish: false, comment: true, view: true, viewTrends: true, manageTrendSources: true },
  approver: { manageWorkspace: false, manageBrands: false, manageMembers: false, manageSso: false, generate: false, generateMedia: false, editDraft: true, approve: true, publish: true, comment: true, view: true, viewTrends: true, manageTrendSources: false },
  viewer: { manageWorkspace: false, manageBrands: false, manageMembers: false, manageSso: false, generate: false, generateMedia: false, editDraft: false, approve: false, publish: false, comment: false, view: true, viewTrends: true, manageTrendSources: false },
} as const;

export type Permission = keyof (typeof ROLE_PERMS)["owner"];

export function can(role: keyof typeof ROLE_PERMS, perm: Permission): boolean {
  return ROLE_PERMS[role][perm];
}

/**
 * Whether a member can access a specific brand. Owner/admin manage the whole
 * workspace (manageWorkspace / manageBrands) and are exempt from per-brand
 * scoping by design — Membership.brandScope is documented as scoping non-admins
 * only. For editor/approver/viewer: an empty brandScope means "all brands";
 * otherwise the brand id must be in the scope list. Defensive: legacy rows
 * written before `brandScope` existed lack the attribute when read from DynamoDB
 * (no zod parse on raw GetItem), so coalesce to [] = "all brands" — safer than
 * 500-ing on `.length` of undefined.
 */
export function canAccessBrand(
  membership: Pick<Membership, "role" | "brandScope">,
  brandId: string,
): boolean {
  if (membership.role === "owner" || membership.role === "admin") return true;
  const scope = membership.brandScope ?? [];
  if (scope.length === 0) return true;
  return scope.includes(brandId);
}
